<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      When we think of fitness, the first thing that springs to mind is running or lifting weights.
      This is because we tend to associate fitness with cardio endurance or our physical strength.
      Though frequently overlooked, stretching is equally as important as the other parts.
    </p>
    <p class="mb-5 text-bold">But Why Is Stretching So Important?</p>
    <p class="mb-5">
      Stretching ensures that we keep our muscles flexible, strong and healthy. Maintaining or
      improving our flexibility helps improve the range of motion in our joints. When we lose this
      range of motion, our muscles shorten and become tighter which increases our risk of
      experiencing pain, strain, and muscle damage.
    </p>
    <p class="mb-5 text-bold">Dynamic Stretching and Static Stretching</p>
    <p class="mb-5">
      Not all stretching is the same - in fact, there are two distinct types: dynamic stretching and
      static stretching.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5 text-bold">Dynamic Stretching</p>
        <p class="mb-5">
          Dynamic stretching works great as part of a warm-up and involves active movement. The
          movements usually mimic the ones used in the sport/exercise a person is preparing to
          perform.
        </p>
        <p class="mb-10">
          eg: a football player would do leg swings before playing to get them ready for the
          activity ahead.
        </p>

        <p class="mb-5 text-bold">Static Stretching</p>
        <p class="mb-5">
          Static stretching is all about stretching a muscle to near its furthest point and then
          holding that position for around 20 seconds.
        </p>
        <p class="mb-5">
          The emphasis is often to focus on a single muscle group with each stretch.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/fitness/stretching.jpg"
          alt=""
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>
    <p class="mb-5 text-italic">
      To find out <strong>much</strong> more about dynamic and static stretching, watch this video:
    </p>
    <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].url" />

    <h4 class="mb-5">Activity: Create a Static Stretching Routine</h4>
    <p class="mb-5">
      In this activity, you're going to do a little research to come up with your own static
      stretching routine for yourself.
    </p>
    <p class="mb-5">
      Static stretching in general is much safer to work with at home, though it should only be done
      after the body has been warmed up through exercise (like running).
    </p>
    <p class="mb-5">
      Start by reading through Stretching: An Illustrated Guide below and watching the video on
      static stretching, 25 Static Stretches.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5 text-center">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: Static stretching"
          @click="showVideoPopup('https://www.youtube.com/embed/7FCNFuyyWTA?autoplay=1')"
        >
          <img
            src="@/assets/images/experiences/fitness/static_stretch_splash.jpg"
            class="mx-auto mb-5 img-responsive"
            alt=""
          />
        </button>
      </div>
      <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Open Stretching: an illustrated guide"
          @click="
            showIframePopup(
              'https://www.mydr.com.au/sports-fitness/stretching-an-illustrated-guide',
            )
          "
        >
          <img
            src="@/assets/images/experiences/fitness/stretching_guide.png"
            class="mx-auto mb-5 img-responsive cursor-pointer"
            alt=""
          />
        </button>
      </div>
    </div>

    <p class="mb-5">
      Using what you’ve learned and researched around stretching, design a sequence of 7
      post-workout static stretches.
    </p>

    <p class="mb-5">It should:</p>

    <ul class="default-list">
      <li>Give a full-body stretch</li>
      <li>Incorporate all the major body parts ( legs, chest, back, neck, arms & shoulders).</li>
    </ul>
    <p class="mb-5">
      Record the name of the stretch in the space provided and upload a selfie of yourself
      performing the stretch. Happy stretching!
    </p>

    <table class="table default-table">
      <tr class="header">
        <th colspan="4">My Post-Workout Static Stretching Routine</th>
      </tr>
      <tr class="subheader">
        <th scope="col" width="50%" colspan="2">Name of Stretch</th>
        <th scope="col" width="50%">Photo</th>
      </tr>
      <tr class="body" v-for="index in 7" :key="'str_' + index">
        <th scope="row" class="subheader" width="5%">{{ index }}</th>
        <td><text-block v-model="data['question' + index]" :editable="isEditable" /></td>
        <td align="center">
          <fileupload-button-immediate
            v-model="uploaded_files['uploaded_file_' + index]"
            :is-editable="isEditable"
            :upload-btn-text="'Upload Photo Here'"
            :index="index"
            filetype="media"
          />
        </td>
      </tr>
    </table>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/stretching_1_splash.jpg'),
          url: 'https://www.youtube.com/embed/yaNMChx6ehI',
        },
        2: {
          image: require('@/assets/images/experiences/fitness/static_stretch_splash.jpg'),
          url: 'https://www.youtube.com/embed/7FCNFuyyWTA',
        },
      },
      image1: require('@/assets/images/experiences/fitness/HIIT-Formula-Infographic-1.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
        uploaded_file_4: '',
        uploaded_file_5: '',
        uploaded_file_6: '',
        uploaded_file_7: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
